<template>
  <div 
    ref="container" 
    class="avatar-3d-container"
    :style="{ left: position.x + 'px', top: position.y + 'px' }"
    @mousedown="startDrag"
  ></div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, reactive } from 'vue'
import * as THREE from 'three'
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js'
import { MTLLoader } from 'three/examples/jsm/loaders/MTLLoader.js'

const container = ref(null)
let scene, camera, renderer, avatar, avatarGroup, logoGroup
let animationId = null

// Position draggable de la scène 3D
const position = reactive({
  x: 0,
  y: 0
})

// Variables pour le drag
let isDragging = false
let dragOffset = { x: 0, y: 0 }

// Initialiser la position au montage
const initializePosition = () => {
  if (!container.value) return
  
  // Calculer la position initiale basée sur les valeurs CSS originales
  const containerWidth = container.value.offsetWidth || 650
  const containerHeight = container.value.offsetHeight || 650
  const windowWidth = window.innerWidth
  const windowHeight = window.innerHeight
  
  // Position initiale : right: 8%, top: 40% (centré verticalement)
  position.x = windowWidth - (windowWidth * 0.08) - containerWidth
  position.y = (windowHeight * 0.4) - (containerHeight / 2)
}

// Gérer le début du drag
const startDrag = (e) => {
  if (!container.value) return
  
  isDragging = true
  const rect = container.value.getBoundingClientRect()
  dragOffset.x = e.clientX - rect.left
  dragOffset.y = e.clientY - rect.top
  
  // Changer le curseur en "grabbing"
  if (container.value) container.value.style.cursor = 'grabbing'
  if (renderer && renderer.domElement) renderer.domElement.style.cursor = 'grabbing'
  
  e.preventDefault()
  document.addEventListener('mousemove', onDrag)
  document.addEventListener('mouseup', stopDrag)
}

// Gérer le drag
const onDrag = (e) => {
  if (!isDragging || !container.value) return
  
  const containerWidth = container.value.offsetWidth || 650
  const containerHeight = container.value.offsetHeight || 650
  const windowWidth = window.innerWidth
  const windowHeight = window.innerHeight
  
  // Calculer la nouvelle position
  let newX = e.clientX - dragOffset.x
  let newY = e.clientY - dragOffset.y
  
  // Limiter la position dans les limites de la fenêtre
  newX = Math.max(0, Math.min(newX, windowWidth - containerWidth))
  newY = Math.max(0, Math.min(newY, windowHeight - containerHeight))
  
  position.x = newX
  position.y = newY
}

// Arrêter le drag
const stopDrag = () => {
  isDragging = false
  
  // Remettre le curseur en "move"
  if (container.value) container.value.style.cursor = 'move'
  if (renderer && renderer.domElement) renderer.domElement.style.cursor = 'move'
  
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
}

// Chemin vers le modèle 3D de l'avatar (à ajuster selon votre fichier)
const avatarModelPath = '/models/IronMan.obj' // ou .gltf

// Liste des logos de technologies à afficher
const techLogos = [
  { path: '/img/icons/projects/tools/vuejs.svg', name: 'Vue.js' },
  { path: '/img/icons/projects/tools/laravel.svg', name: 'Laravel' },
  { path: '/img/icons/projects/tools/javascript.svg', name: 'JavaScript' },
  { path: '/img/icons/projects/tools/docker.svg', name: 'Docker' },
  { path: '/img/icons/projects/tools/kubernetes.svg', name: 'Kubernetes' },
  { path: '/img/icons/projects/tools/tailwind.svg', name: 'Tailwind' },
  { path: '/img/icons/projects/tools/php.svg', name: 'PHP' },
  { path: '/img/icons/projects/tools/bash.svg', name: 'Bash' }
]

const init = () => {
  if (!container.value) return

  // Créer la scène avec background transparent
  scene = new THREE.Scene()
  scene.background = null // Fond transparent pour se fondre avec l'image de background

  // Créer la caméra
  camera = new THREE.PerspectiveCamera(75, container.value.clientWidth / container.value.clientHeight, 0.1, 1000)
  camera.position.z = 6 // Ajuster la position de la caméra pour mieux voir l'avatar et les logos

  // Créer le renderer avec fond transparent et qualité optimale
  renderer = new THREE.WebGLRenderer({
    alpha: true,
    antialias: true,
    premultipliedAlpha: false,
    powerPreference: 'high-performance'
  })
  renderer.setClearColor(0x000000, 0) // Fond transparent
  renderer.outputColorSpace = THREE.SRGBColorSpace // Important pour préserver les couleurs
  renderer.toneMapping = THREE.ACESFilmicToneMapping // Tone mapping élégant
  renderer.toneMappingExposure = 1.0
  renderer.shadowMap.enabled = false // Pas d'ombres pour un design épuré
  renderer.setSize(container.value.clientWidth, container.value.clientHeight)
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)) // Limiter pour performance
  container.value.appendChild(renderer.domElement)
  
  // Permettre le drag même en cliquant directement sur le canvas 3D
  renderer.domElement.style.pointerEvents = 'auto'
  renderer.domElement.style.cursor = 'move'
  renderer.domElement.addEventListener('mousedown', startDrag)

  // Créer un groupe pour l'avatar
  avatarGroup = new THREE.Group()
  scene.add(avatarGroup)

  // Charger l'avatar 3D (format OBJ) avec textures et couleurs
  const objLoader = new OBJLoader()

  // Vérifier s'il y a un fichier .mtl (matériaux)
  const mtlPath = avatarModelPath.replace('.obj', '.mtl').replace('/models/', '')
  const objFileName = avatarModelPath.replace('/models/', '')

  // Essayer de charger les matériaux d'abord, sinon charger directement l'OBJ
  const mtlLoader = new MTLLoader()

  // Configurer le chemin des textures pour le MTLLoader
  // Le MTLLoader chargera automatiquement les textures référencées dans le .mtl
  mtlLoader.setPath('/models/')
  mtlLoader.setMaterialOptions({
    side: THREE.FrontSide,
    wrap: THREE.RepeatWrapping
  })

  console.log('Tentative de chargement du fichier MTL:', mtlPath)

  mtlLoader.load(
    mtlPath,
    (materials) => {
      console.log('Fichier MTL chargé avec succès:', materials)

      // Préparer les matériaux et charger les textures
      materials.preload()

      // Configurer les matériaux pour préserver les couleurs et textures
      if (materials.materialsArray) {
        console.log('Nombre de matériaux trouvés:', materials.materialsArray.length)
        materials.materialsArray.forEach((material) => {
          if (material.map) {
            material.map.colorSpace = THREE.SRGBColorSpace
            console.log('Texture chargée:', material.map.image?.src || 'image')
          }
          if (material.normalMap) {
            material.normalMap.colorSpace = THREE.SRGBColorSpace
          }
          if (material.emissiveMap) {
            material.emissiveMap.colorSpace = THREE.SRGBColorSpace
          }
          // Préserver les couleurs du matériau (déjà en SRGB)
        })
      }

      // Charger l'OBJ avec les matériaux
      objLoader.setMaterials(materials)
      objLoader.setPath('/models/')
      objLoader.load(
        objFileName,
        (object) => {
          avatar = object

          // S'assurer que tous les matériaux préservent les couleurs et sont appliqués
          avatar.traverse((child) => {
            if (child instanceof THREE.Mesh) {
              if (!child.material) {
                // Si pas de matériau, créer un matériau par défaut
                child.material = new THREE.MeshStandardMaterial({
                  color: 0x888888,
                  metalness: 0.3,
                  roughness: 0.7
                })
              } else if (Array.isArray(child.material)) {
                // Si tableau de matériaux, s'assurer qu'ils sont tous configurés
                child.material.forEach((mat) => {
                  if (mat) {
                    if (mat.map) mat.map.colorSpace = THREE.SRGBColorSpace
                    // Forcer la mise à jour du matériau
                    mat.needsUpdate = true
                    // S'assurer que la couleur est visible (les couleurs du MTL sont déjà en RGB)
                    // Pas besoin de conversion, les couleurs sont déjà correctes
                  }
                })
              } else {
                // Matériau unique
                if (child.material.map) {
                  child.material.map.colorSpace = THREE.SRGBColorSpace
                }
                // Forcer la mise à jour
                child.material.needsUpdate = true
                // S'assurer que la couleur est visible (les couleurs du MTL sont déjà en RGB)
                // Pas besoin de conversion, les couleurs sont déjà correctes
                // S'assurer que le matériau est visible
                if (child.material.transparent === undefined) {
                  child.material.transparent = false
                }
              }
            }
          })

          setupAvatar()
        },
        (progress) => {
          if (progress.lengthComputable) {
            console.log('Chargement avatar:', (progress.loaded / progress.total) * 100 + '%')
          }
        },
        (error) => {
          console.error("Erreur lors du chargement de l'avatar OBJ:", error)
          loadOBJWithoutMaterials()
        }
      )
    },
    undefined,
    (error) => {
      // Si le fichier .mtl n'existe pas ou erreur de chargement
      console.warn("Fichier MTL non trouvé ou erreur de chargement, chargement de l'OBJ sans matériaux:", error)
      loadOBJWithoutMaterials()
    }
  )

  // Fonction pour charger l'OBJ sans matériaux (avec matériau par défaut)
  const loadOBJWithoutMaterials = () => {
    objLoader.setPath('/models/')
    objLoader.load(
      objFileName,
      (object) => {
        avatar = object

        // Préserver les couleurs existantes si présentes, sinon appliquer un matériau par défaut
        avatar.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            // Si le mesh a déjà un matériau avec des couleurs, les préserver
            if (child.material) {
              // Préserver les couleurs existantes
              if (Array.isArray(child.material)) {
                child.material.forEach((mat) => {
                  if (mat) {
                    if (mat.map) mat.map.colorSpace = THREE.SRGBColorSpace
                    // Les couleurs du MTL sont déjà en RGB, pas besoin de conversion
                    mat.needsUpdate = true
                  }
                })
              } else {
                if (child.material.map) child.material.map.colorSpace = THREE.SRGBColorSpace
                // Les couleurs du MTL sont déjà en RGB, pas besoin de conversion
                child.material.needsUpdate = true
              }
            } else {
              // Appliquer un matériau épuré et élégant si aucun matériau n'existe
              child.material = new THREE.MeshStandardMaterial({
                color: 0xe8e8e8,
                metalness: 0.1,
                roughness: 0.4,
                envMapIntensity: 1.0
              })
            }
          }
        })

        setupAvatar()
      },
      (progress) => {
        if (progress.lengthComputable) {
          console.log('Chargement avatar:', (progress.loaded / progress.total) * 100 + '%')
        }
      },
      (error) => {
        console.error("Erreur lors du chargement de l'avatar OBJ:", error)
        // Fallback: créer une sphère si le modèle ne charge pas
        const avatarGeometry = new THREE.SphereGeometry(0.8, 32, 32)
        const avatarMaterial = new THREE.MeshStandardMaterial({
          color: 0x4a90e2,
          metalness: 0.8,
          roughness: 0.2
        })
        avatar = new THREE.Mesh(avatarGeometry, avatarMaterial)
        avatarGroup.add(avatar)
      }
    )
  }

  // Fonction pour configurer l'avatar (taille, position, etc.)
  const setupAvatar = () => {
    // Ajuster la taille et la position de l'avatar
    const box = new THREE.Box3().setFromObject(avatar)
    const size = box.getSize(new THREE.Vector3())
    const maxDim = Math.max(size.x, size.y, size.z)
    const scale = 3.5 / maxDim // Réduire la taille de l'avatar (réduit de 5.0 à 3.5)
    avatar.scale.multiplyScalar(scale)

    // Centrer l'avatar
    const center = box.getCenter(new THREE.Vector3())
    avatar.position.x = -center.x * scale
    avatar.position.y = -center.y * scale
    avatar.position.z = -center.z * scale

    avatarGroup.add(avatar)
  }

  // Créer un groupe pour les logos
  logoGroup = new THREE.Group()
  scene.add(logoGroup)

  // Charger et créer les logos
  const loader = new THREE.TextureLoader()
  const logoMeshes = []

  techLogos.forEach((logo, index) => {
    loader.load(
      logo.path,
      (texture) => {
        // Configurer la texture pour les SVG
        texture.flipY = false
        texture.minFilter = THREE.LinearFilter
        texture.magFilter = THREE.LinearFilter

        const logoGeometry = new THREE.PlaneGeometry(0.65, 0.65) // Taille optimisée

        // Configurer la texture pour préserver les couleurs
        texture.colorSpace = THREE.SRGBColorSpace // Important pour préserver les couleurs

        const logoMaterial = new THREE.MeshStandardMaterial({
          map: texture,
          transparent: true,
          side: THREE.DoubleSide,
          alphaTest: 0.01,
          color: 0xffffff,
          metalness: 0.0,
          roughness: 0.1,
          emissive: 0x000000,
          emissiveIntensity: 0.0
        })
        const logoMesh = new THREE.Mesh(logoGeometry, logoMaterial)

        // Positionner les logos en cercle autour de l'avatar
        const angle = (index / techLogos.length) * Math.PI * 2
        const radius = 3.0 // Ajuster la distance des logos (réduit de 3.5 à 3.0 pour éviter les débordements)
        logoMesh.position.x = Math.cos(angle) * radius
        logoMesh.position.y = Math.sin(angle) * radius
        logoMesh.position.z = 0

        // Faire face à la caméra
        logoMesh.lookAt(camera.position)

        logoGroup.add(logoMesh)
        logoMeshes.push(logoMesh)
      },
      undefined,
      (error) => {
        console.error('Erreur lors du chargement du logo:', logo.path, error)
      }
    )
  })

  // Éclairage épuré et professionnel
  const ambientLight = new THREE.AmbientLight(0xffffff, 0.7)
  scene.add(ambientLight)

  // Lumière principale douce
  const directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.5)
  directionalLight1.position.set(4, 4, 4)
  scene.add(directionalLight1)

  // Lumière d'appoint subtile
  const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.3)
  directionalLight2.position.set(-4, 2, 4)
  scene.add(directionalLight2)

  // Lumière de remplissage douce
  const pointLight = new THREE.PointLight(0xffffff, 0.4, 100)
  pointLight.position.set(0, 0, 5)
  scene.add(pointLight)

  // Fonction d'animation
  const animate = () => {
    animationId = requestAnimationFrame(animate)

    // Animation fluide et élégante de l'avatar (rotation lente sur lui-même)
    if (avatarGroup && avatarGroup.children.length > 0) {
      avatarGroup.rotation.y += 0.002 // Rotation très lente de l'avatar
    }

    // Animation fluide des logos autour de l'avatar (rotation plus rapide)
    if (logoGroup) {
      logoGroup.rotation.y += 0.008 // Rotation plus rapide des logos autour de l'avatar

      // Faire face à la caméra en continu avec transition douce
      logoGroup.children.forEach((logo, index) => {
        if (logo instanceof THREE.Mesh) {
          // Rotation subtile pour un effet plus élégant
          logo.rotation.z += 0.001
          // Faire face à la caméra en continu
          logo.lookAt(camera.position)
        }
      })
    }

    renderer.render(scene, camera)
  }

  animate()

  // Gérer le redimensionnement
  const handleResize = () => {
    if (!container.value) return

    camera.aspect = container.value.clientWidth / container.value.clientHeight
    camera.updateProjectionMatrix()
    renderer.setSize(container.value.clientWidth, container.value.clientHeight)
  }

  window.addEventListener('resize', handleResize)

  // Stocker la fonction de nettoyage
  return () => {
    window.removeEventListener('resize', handleResize)
  }
}

let cleanupResize = null
let handleWindowResize = null

const cleanup = () => {
  if (animationId) {
    cancelAnimationFrame(animationId)
  }
  if (cleanupResize) {
    cleanupResize()
  }
  if (renderer && renderer.domElement) {
    renderer.domElement.removeEventListener('mousedown', startDrag)
  }
  if (renderer && container.value && renderer.domElement) {
    try {
      container.value.removeChild(renderer.domElement)
    } catch (e) {
      // Element already removed
    }
  }
  if (scene) {
    scene.traverse((object) => {
      if (object.geometry) object.geometry.dispose()
      if (object.material) {
        if (Array.isArray(object.material)) {
          object.material.forEach((material) => material.dispose())
        } else {
          object.material.dispose()
        }
      }
      if (object.texture) object.texture.dispose()
    })
  }
}

onMounted(() => {
  cleanupResize = init()
  // Initialiser la position après un court délai pour s'assurer que le container est rendu
  setTimeout(() => {
    initializePosition()
  }, 100)
  
  // Réinitialiser la position lors du redimensionnement de la fenêtre
  handleWindowResize = () => {
    if (!isDragging) {
      // Ajuster la position pour rester dans les limites si nécessaire
      if (container.value) {
        const containerWidth = container.value.offsetWidth || 650
        const containerHeight = container.value.offsetHeight || 650
        const windowWidth = window.innerWidth
        const windowHeight = window.innerHeight
        
        position.x = Math.max(0, Math.min(position.x, windowWidth - containerWidth))
        position.y = Math.max(0, Math.min(position.y, windowHeight - containerHeight))
      }
    }
  }
  
  window.addEventListener('resize', handleWindowResize)
})

onUnmounted(() => {
  cleanup()
  stopDrag() // S'assurer que le drag est arrêté
  if (handleWindowResize) {
    window.removeEventListener('resize', handleWindowResize)
  }
})
</script>

<style scoped>
.avatar-3d-container {
  position: absolute;
  width: 650px;
  height: 650px;
  pointer-events: auto;
  cursor: move;
  z-index: 1;
  opacity: 0.95;
  overflow: hidden;
  filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.1)); /* Ombre douce et élégante */
  transition: opacity 0.3s ease;
  user-select: none;
}

.avatar-3d-container:active {
  cursor: grabbing;
}

@media (max-width: 1024px) {
  .avatar-3d-container {
    width: 550px;
    height: 550px;
  }
}

@media (max-width: 768px) {
  .avatar-3d-container {
    width: 450px;
    height: 450px;
  }
}

@media (max-width: 480px) {
  .avatar-3d-container {
    width: 350px;
    height: 350px;
  }
}
</style>
